import React, { useState, useEffect } from 'react';
import cn from 'classnames';
import Button, {
  ButtonType,
} from '../../../../../../../../../components/common/button';
import { useDidMount } from '../../../../../../../../../components/common/effects';
import { Visibility } from '../../../../../../../../../types/common-types';
import './style.scss';

interface CalculatorValue {
  amount: string;
  currentBalance?: string;
}
interface Labels {
  header: string;
  loanAmount: string;
  loanInterest: string;
  disbursementAmountLabel: string;
  loanInterestDescr: string;
  dateOfReturn: string;
  editButton: string;
  saveButton: string;
  selectLoanAmount: string;
  exampleHeader: string;
  exampleTitle: string;
  exampleTotalCosts: string;
  exampleTotalRefund: string;
  exampleDebtRate: string;
  exampleDebtRateValue: string;
  exampleAPR: string;
  exampleAPRValue: string;
  exampleDescr: string;
}
interface Settings {
  amountOptions: {
    [key: string]: {
      installmentDue: number;
      totalDue: number;
    };
  };
  amount: { min: number; max: number; step: number };
  duration: { value: number };
}
interface Props {
  id: string;
  visibility?: Visibility;
  value: CalculatorValue;
  extra: {
    labels: Labels;
    settings: Settings;
    defaultEditMode: boolean;
  };
  onSubmit: (id: string) => void;
  onChange: (
    value: {
      id: string;
      value: object;
      error: boolean;
    },
    callback?: () => void
  ) => void;
}

const Calculator: React.FC<Props> = ({
  id,
  visibility,
  extra,
  value,
  onChange,
  onSubmit,
}) => {
  const { labels, settings, defaultEditMode = false } = extra;
  const {
    header,
    editButton,
    saveButton,
    loanAmount,
    loanInterest,
    disbursementAmountLabel,
    selectLoanAmount,
    dateOfReturn,
    exampleHeader,
    exampleTotalCosts,
    exampleTotalRefund,
    exampleDebtRate,
    exampleDebtRateValue,
    exampleAPR,
    exampleAPRValue,
    exampleDescr,
  } = labels;
  const {
    amountOptions,
    duration: settingsDuration,
    amount: settingsAmount,
  } = settings;
  const [editMode, setEditMode] = useState(defaultEditMode);
  const { amount, currentBalance } = value;
  const disbursementAmountVisible = !!currentBalance;
  const disbursementAmount = currentBalance
    ? (Math.round((+amount - +currentBalance) * 100) / 100).toFixed(2)
    : 0;
  const { value: duration } = settingsDuration;
  const didMount = useDidMount();
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (didMount) onSubmit(id);
    }, 500);
    return () => clearTimeout(timeout);
  }, [value]);

  const handleChange = (key: string, val: string) => {
    const newData = { ...value, [key]: val };
    onChange({ id, value: newData, error: false });
  };

  const addZero = (num: number) => {
    return num < 10 ? `0${num}` : num;
  };

  const interest = amountOptions[`${amount}`].installmentDue;
  const totalRefund = amountOptions[`${amount}`].totalDue;
  const totalCosts = (totalRefund - +amount).toFixed(2);
  const returnDate = new Date();
  returnDate.setDate(returnDate.getDate() + Number(duration) + 1);
  const returnDatestr = `${returnDate.getDate()}.${addZero(
    returnDate.getMonth() + 1
  )}.${returnDate.getFullYear()}`;
  const currency = 'kr.';
  return (
    <div
      id={id}
      data-cy="calculator"
      styleName={cn('calculator', {
        hidden: visibility === Visibility.Hidden,
      })}
    >
      <div styleName="calculator__header">
        <span>{header}</span>
        <Button
          id="calculatorButton"
          styleName={cn('calculator__button', {
            edit: editMode,
            hidden: visibility === Visibility.Disabled,
          })}
          type={ButtonType.Normal}
          label={editMode ? saveButton : editButton}
          onClick={() => {
            setEditMode(!editMode);
          }}
        />
      </div>
      <div styleName="calculator__divider" />
      {editMode ? (
        <div styleName="calculator__editor">
          <div styleName="calculator__editor__item">
            <div styleName="calculator__editor__header">
              <span>{selectLoanAmount}</span>
              <span styleName="calculator__editor__value">
                {`${amount} ${currency}`}
              </span>
            </div>
            <div styleName="calculator__editor__slider">
              <input
                type="range"
                data-cy-prop="value.amount"
                min={settingsAmount.min}
                max={settingsAmount.max}
                step={settingsAmount.step}
                value={amount}
                onChange={e => handleChange('amount', e.target.value)}
              />
            </div>
            <div styleName="calculator__editor__footer">
              <span>{`${settingsAmount.min} ${currency}`}</span>
              <span>{`${settingsAmount.max} ${currency}`}</span>
            </div>
          </div>
        </div>
      ) : null}
      <div styleName="calculator__summary">
        <div styleName="calculator__row">
          <span styleName="calculator__row__lbl">{loanAmount}</span>
          <span styleName="calculator__row__val">{`${amount} ${currency}`}</span>
        </div>
        <div styleName="calculator__row">
          <span styleName="calculator__row__lbl">{loanInterest}</span>
          <span styleName="calculator__row__val">
            {`${interest} ${currency}`}
          </span>
        </div>
        {disbursementAmountVisible ? (
          <div styleName="calculator__row">
            <span styleName="calculator__row__lbl">
              {disbursementAmountLabel}
            </span>
            <span styleName="calculator__row__val">
              {`${disbursementAmount} ${currency}`}
            </span>
          </div>
        ) : null}
        <div styleName="calculator__row">
          <span styleName="calculator__row__lbl">{dateOfReturn}</span>
          <span styleName="calculator__row__val bold">{returnDatestr}</span>
        </div>
      </div>
      <div styleName="calculator__example">
        <div styleName="calculator__example__row">
          <b>{exampleHeader}</b>
        </div>
        <div styleName="calculator__example__row">
          <div styleName="calculator__example__tbl">
            <span>{exampleTotalCosts}</span>
            <span styleName="calculator__example__tbl__val">
              <b>{`${totalCosts} ${currency}`}</b>
            </span>
          </div>
          <div styleName="calculator__example__tbl">
            <span>{exampleTotalRefund}</span>
            <span>
              <b>{`${totalRefund} ${currency}`}</b>
            </span>
          </div>
          <div styleName="calculator__example__tbl">
            <span>{exampleDebtRate}</span>
            <span>
              <b>{exampleDebtRateValue}</b>
            </span>
          </div>
          <div styleName="calculator__example__tbl">
            <span>{exampleAPR}</span>
            <span styleName="calculator__example__tbl__val">
              <b>{exampleAPRValue}</b>
            </span>
          </div>
        </div>
        <div styleName="calculator__info">
          <div styleName="calculator__example__row">{exampleDescr}</div>
        </div>
      </div>
    </div>
  );
};

export default Calculator;
